import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { readAudienceClaim, hasAdviserLink } from '@/lib/auth/audience';

// External-adviser identity resolver (Phase 3, F2).
//
// Mirrors `useSurfaceAccess`: a small, independently-cached React-Query hook that never
// touches the shared `useAuth` state machine (it only READS from it). It answers a single
// question — "is the current principal an external adviser (an IFA), as opposed to staff,
// retail, or anonymous?".
//
// Eligibility gate: `!!user && !!session && !staffMember`. Anyone with a staffMember is
// EXCLUDED — including the 11 internal `role_level='adviser'` staff, who all carry a
// `staff_member_id` and therefore resolve a staffMember. So the adviser path can never
// fire for staff, by construction.
//
// Resolution (only runs when eligible):
//   1. Claim-first — `readAudienceClaim(session)` reads the JWT `audience` claim (network-free).
//   2. DB fallback — `hasAdviserLink()` queries the caller's own `adviser_links` row, but ONLY
//      when the claim did not already say 'adviser'. Before the hook is registered the claim is
//      always null, so the fallback is what makes the feature work on S1 (tables) alone.
//
// Inert until data exists: with no adviser_links rows and no hook, every current user is either
// ineligible (staff/anon) or resolves `isAdviser: false`. Zero behavioural delta.

export type AdviserIdentitySource = 'claim' | 'link' | 'none';

export interface AdviserIdentity {
  loading: boolean;
  isAdviser: boolean;
  source: AdviserIdentitySource;
}

export function useAdviserIdentity(): AdviserIdentity {
  const { user, session, staffMember, loading: authLoading } = useAuth();

  // Staff (incl. internal advisers) and anon are ineligible: they never take the adviser path.
  const eligible = !!user && !!session && !staffMember;

  // Cheap, synchronous claim read — no network. Null until the hook (S6) is registered.
  const claimIsAdviser = eligible ? readAudienceClaim(session) === 'adviser' : false;

  const query = useQuery({
    // Key on the user id so the answer caches per principal and clears on sign-out/switch.
    queryKey: ['adviser_identity', user?.id ?? null],
    // Only ever run the DB fallback when we're eligible AND the claim didn't already resolve it.
    enabled: eligible && !claimIsAdviser,
    staleTime: 5 * 60 * 1000,
    queryFn: hasAdviserLink,
  });

  // Still loading if core auth is settling, or the fallback query is in flight.
  const loading = authLoading || (eligible && !claimIsAdviser && query.isLoading);

  if (!eligible) {
    return { loading, isAdviser: false, source: 'none' };
  }

  if (claimIsAdviser) {
    return { loading, isAdviser: true, source: 'claim' };
  }

  const linkIsAdviser = query.data === true;
  return {
    loading,
    isAdviser: linkIsAdviser,
    source: linkIsAdviser ? 'link' : 'none',
  };
}
